import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { apiAgent } from '../../../axiosConfig'
import { InputBox } from '../../../design_system/components/Input Fields/InputBox.jsx'
import { InputArea } from '../../../design_system/components/Input Fields/InputArea.jsx'
import { SearchableSelectBox } from '../../../design_system/components/Input Fields/SearchableSelectBox.jsx'
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx'
import { Loader } from '../../../design_system/components/Loader.jsx'
import { updateCurrentTeam } from '../../../redux/currentTeamSlice'
import { useSettingsContext, settingsSnapshot } from './SettingsContext'
import { ALL_TIME_ZONES } from '../../utils/timeUtils'



const TeamSettings = ({ setActiveSettingTab }) => {
    const [formData, setFormData] = useState({
        name: '',
        linkedin_url: '',
        team_time_zone: '',
        aliases: '',
    })
    const [errors, setErrors] = useState({})
    const [isDataLoaded, setIsDataLoaded] = useState(false)
    const toast = useToast()
    const dispatch = useDispatch()
    const { registerTab, unregisterTab, setHasChanges } = useSettingsContext()
    const snapshotRef = useRef(null)

    useEffect(() => {
        setActiveSettingTab('team_settings')
        apiAgent({ method: 'get', url: '/team/team_details', params: {} })
            .then(res => {
                const d = res.data.data
                const loaded = {
                    name:           d.name           || '',
                    linkedin_url:   d.linkedin_url   || '',
                    team_time_zone: d.team_time_zone || '',
                    aliases:        (d.aliases || []).join(', '),
                }
                setFormData(loaded)
                snapshotRef.current = settingsSnapshot(loaded)
                setIsDataLoaded(true)
            })
            .catch(() => { })
    }, [])

    const handleChange = (name, value) =>
        setFormData(prev => ({ ...prev, [name]: value }))

    // ── Change detection ──────────────────────────────────────────────────────

    useEffect(() => {
        if (snapshotRef.current === null) return
        setHasChanges(settingsSnapshot(formData) !== snapshotRef.current)
    }, [formData, setHasChanges])

    // ── Save ─────────────────────────────────────────────────────────────────

    const handleSubmit = useCallback(() => {
        if (!formData.name.trim()) {
            setErrors({ name: 'Team name is required' })
            return Promise.reject()
        }
        setErrors({})
        return apiAgent({
            method: 'post',
            url: '/team/update_general',
            data: {
                name:           formData.name.trim(),
                linkedin_url:   formData.linkedin_url.trim(),
                team_time_zone: formData.team_time_zone,
                aliases:        formData.aliases
                                    .split(',')
                                    .map(a => a.trim())
                                    .filter(Boolean),
            },
        })
            .then(res => {
                dispatch(updateCurrentTeam(res.data.team))
                toast.success('Changes saved')
                snapshotRef.current = settingsSnapshot(formData)
                setHasChanges(false)
            })
            .catch(() => toast.error('Something went wrong. Please try again.'))
    }, [formData, dispatch, toast, setHasChanges])

    const handleCancel = useCallback(() => {
        if (snapshotRef.current) {
            const saved = JSON.parse(snapshotRef.current)
            // Restore with '' fallback since normalize converts '' → null
            setFormData({
                name:           saved.name           || '',
                linkedin_url:   saved.linkedin_url   || '',
                team_time_zone: saved.team_time_zone || '',
                aliases:        saved.aliases        || '',
            })
        }
        setErrors({})
    }, [])

    // ── Register with settings footer ────────────────────────────────────────
    // Use refs so the effect only runs once (mount/unmount).
    // Without refs, handleSubmit changes on every formData keystroke,
    // triggering unregisterTab() which resets hasChanges → footer flickers.

    const submitRef = useRef(handleSubmit)
    const cancelRef = useRef(handleCancel)
    submitRef.current = handleSubmit
    cancelRef.current = handleCancel

    useEffect(() => {
        const id = registerTab({
            onSave: (...args) => submitRef.current(...args),
            onCancel: (...args) => cancelRef.current(...args),
        })
        return () => unregisterTab(id)
    }, [registerTab, unregisterTab])

    return (
        <div className="settings-panel">
            <div className="settings-panel__header">
                <div className="body-l-bold settings-panel__title">Settings</div>
                <div className="body-m settings-panel__description">
                    Manage your team's profile, and configuration
                </div>
            </div>

            {!isDataLoaded ? <Loader isLoading Size="Medium" /> :
                <div className="settings-panel__section">
                    <span className="body-m-bold settings-panel__section-title">General</span>

                    <div className="settings-panel__fields">
                        <InputBox
                            inputStyle={{ maxWidth: 480 }}
                            label="Team Name"
                            showLabel
                            value={formData.name}
                            handleOnChange={e => handleChange('name', e.target.value)}
                            placeholder="New York University"
                            error={errors.name}
                        />
                        <InputBox
                            inputStyle={{ maxWidth: 480 }}
                            label="LinkedIn URL"
                            showLabel
                            value={formData.linkedin_url}
                            handleOnChange={e => handleChange('linkedin_url', e.target.value)}
                            placeholder="Enter your LinkedIn URL"
                        />
                        <SearchableSelectBox
                            Size="Medium"
                            variant="ghost"
                            triggerStyle={{ border: '1px solid var(--color-border-subtle)' }}
                            label="Timezone"
                            showLabel
                            value={formData.team_time_zone}
                            options={ALL_TIME_ZONES}
                            handleOnChange={val => handleChange('team_time_zone', val)}
                            placeholder="Select timezone"
                            searchPlaceholder="Search timezone..."
                            customStyle={{ maxWidth: 400 }}
                        />
                        <InputArea
                            inputStyle={{ maxWidth: 480 }}
                            label="Team Aliases"
                            showLabel
                            value={formData.aliases}
                            handleOnChange={e => handleChange('aliases', e.target.value)}
                            placeholder="Enter alternate names for this team"
                            rows={3}
                        />
                        <span className="label-s color-text-secondary">Comma separated</span>
                    </div>
                </div>}
        </div>
    )
}

export default TeamSettings
